import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const location = useLocation();

  const isActive = (path) => location.pathname === path;


  /* ==========================================
     Main Navigation Links
  ========================================== */

  const navLinks = [
    {
      name: 'Home',
      path: '/',
    },

    {
      name: 'About',
      path: '/about',
    },

    {
      name: 'Our Products',
      dropdown: [
        {
          name: 'Firewall / Router',
          path: '/our-product?category=firewall',
        },

        {
          name: 'Switches',
          path: '/our-product?category=switches',
        },

        {
          name: 'Network Infrastructure',
          path: '/our-product?category=infrastructure',
        },
      ],
    },

    {
      name: 'Contact Us',
      path: '/contact-us',
    },

    {
      name: 'Raise Service Request',
      path: '/raise-service-request',
    },

    {
      name: 'Track Request',
      path: '/track-request',
    },
  ];


  /* ==========================================
     More Menu
  ========================================== */

  const moreLinks = [
    {
      name: 'Secure SD-WAN',
      path: '/secure-sd-wan',
    },

    {
      name: 'Customer Feedback',
      path: '/customer-feedback',
    },

    {
      name: 'Careers',
      path: '/career',
    },
  ];


  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex justify-between items-center h-20">


          {/* ==========================================
              Logo + Business Portals
          ========================================== */}

          <div className="flex items-center space-x-4">

            {/* Logo */}

            <Link
              to="/"
              className="flex items-center flex-shrink-0"
            >

              <img
                src="https://img1.wsimg.com/isteam/ip/a27d2dc3-82db-4815-b271-215705ee1c2c/favikon%20logo%202.jpg/:/rs=h:75,cg:true,m/qt=q:95"
                alt="IndyaNet"
                className="h-12 w-auto"
              />

            </Link>


            {/* ==========================================
                CRM Business Portal Button
            ========================================== */}

            <div className="hidden lg:flex items-center gap-2">

              {/* CRM Button */}

              <a
                href="https://crm.indyanet.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3.5 py-2 rounded-md text-xs font-bold tracking-wide text-red-600 bg-white border border-red-500 hover:bg-red-600 hover:text-white shadow-sm hover:shadow-md transition-all duration-200"
              >
                CRM
              </a>

            </div>

          </div>


          {/* ==========================================
              Desktop Navigation
          ========================================== */}

          <div className="hidden md:flex items-center space-x-1">

            {navLinks.map((link) =>

              link.dropdown ? (

                <div
                  key={link.name}
                  className="relative group"
                >

                  <button
                    type="button"
                    className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-gray-50 flex items-center transition-colors"
                  >

                    {link.name}

                    <ChevronDown className="ml-1 h-4 w-4" />

                  </button>


                  {/* Dropdown */}

                  <div className="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">

                    {link.dropdown.map((item) => (

                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-4 py-3 text-sm text-gray-700 hover:text-red-600 hover:bg-gray-50"
                      >

                        {item.name}

                      </Link>

                    ))}

                  </div>

                </div>

              ) : (

                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                  }`}
                >

                  {link.name}

                </Link>

              )

            )}


            {/* ==========================================
                More Dropdown
            ========================================== */}

            <div className="relative">

              <button
                type="button"
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-gray-50 flex items-center transition-colors"
              >

                More

                <ChevronDown className="ml-1 h-4 w-4" />

              </button>


              {isMoreOpen && (

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200">

                  {moreLinks.map((link) => (

                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMoreOpen(false)}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isActive(link.path)
                          ? 'text-red-600 bg-red-50'
                          : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                      }`}
                    >

                      {link.name}

                    </Link>

                  ))}

                </div>

              )}

            </div>

          </div>


          {/* ==========================================
              Mobile Menu Button
          ========================================== */}

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-red-600 hover:bg-gray-50 transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >

            {isOpen ? (

              <X className="h-6 w-6" />

            ) : (

              <Menu className="h-6 w-6" />

            )}

          </button>

        </div>

      </div>


      {/* ==========================================
          Mobile Navigation
      ========================================== */}

      {isOpen && (

        <div className="md:hidden border-t border-gray-200 bg-white">

          <div className="px-2 pt-2 pb-3 space-y-1">

            {navLinks.map((link) =>

              link.dropdown ? (

                <div key={link.name}>

                  <div className="px-3 py-2 text-base font-medium text-gray-700">

                    {link.name}

                  </div>


                  <div className="pl-4">

                    {link.dropdown.map((item) => (

                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-md"
                      >

                        {item.name}

                      </Link>

                    ))}

                  </div>

                </div>

              ) : (

                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                  }`}
                >

                  {link.name}

                </Link>

              )

            )}


            {/* ==========================================
                Mobile More Links
            ========================================== */}

            {moreLinks.map((link) => (

              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-red-600 bg-red-50'
                    : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                }`}
              >

                {link.name}

              </Link>

            ))}


            {/* ==========================================
                Mobile CRM Portal Button
            ========================================== */}

            <div className="pt-3 mt-2 border-t border-gray-200">

              <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Business Portals
              </p>


              <div className="grid grid-cols-2 gap-3 px-3">

                {/* CRM */}

                <a
                  href="https://crm.indyanet.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-bold tracking-wide text-red-600 bg-white border border-red-500 hover:bg-red-600 hover:text-white shadow-sm hover:shadow-md transition-all duration-200"
                >
                  CRM
                </a>

              </div>

            </div>

          </div>

        </div>

      )}

    </nav>
  );
};

export default Navbar;